import type { AuthorizationParams } from '@auth0/auth0-react';

interface RequiredAuthorizationParams extends AuthorizationParams {
  audience: Required<AuthorizationParams['audience']>;
  scope: Required<AuthorizationParams['scope']>;
  redirect_uri: Required<AuthorizationParams['redirect_uri']>;
}

interface Auth0Environment {
  domain: string;
  clientId: string;
  authorizationParams: RequiredAuthorizationParams;
}

export interface Environment {
  apiUrl: string;
  auth0: Auth0Environment;
  authDev?: boolean;
  dataCy: boolean;
  isDev: boolean;
  mapBox: { accessToken: string };
  scanDashboardUrl: string;
  useMockApi?: boolean;
  trenchviewerFrontendUrl: string;
  unifiedApiUrl: string;
  muiProLicenceKey: string;
  oneDriveUrl: string;
  flagsmithEnvId: string;
  flagsmithListeningInterval: number;
  basePath: string;
}

// All custom variables defined in import.meta.env can be found in your local
// .env.[mode] file with mode = development | test | production

const parseEnvBoolean = (value: string | boolean | 'true' | 'false' | undefined) => {
  if (value === undefined) return;
  return value === 'true' || value === true;
};

const getBuildEnvironment = () => {
  return {
    VITE_AUTH0_DOMAIN: import.meta.env.VITE_AUTH0_DOMAIN,
    VITE_AUTH0_CLIENT_ID: import.meta.env.VITE_AUTH0_CLIENT_ID,
    VITE_AUTH0_AUDIENCE: import.meta.env.VITE_AUTH0_AUDIENCE,
    VITE_AUTH0_SCOPE: import.meta.env.VITE_AUTH0_SCOPE,
    VITE_AUTH_DEV: parseEnvBoolean(import.meta.env.VITE_AUTH_DEV),
    VITE_MAPBOX_ACCESS_TOKEN: import.meta.env.VITE_MAPBOX_ACCESS_TOKEN,
    VITE_API_URL: import.meta.env.VITE_API_URL,
    VITE_GRAFANA_SCAN_DASHBOARD_URL: import.meta.env.VITE_GRAFANA_SCAN_DASHBOARD_URL,
    VITE_DATACY: import.meta.env.VITE_DATACY,
    VITE_ENABLE_MOCK_API: import.meta.env.VITE_ENABLE_MOCK_API,
    VITE_TRENCHVIEWER_FRONTEND_URL: import.meta.env.VITE_TRENCHVIEWER_FRONTEND_URL,
    VITE_API_BACKOFFICE: import.meta.env.VITE_API_BACKOFFICE,
    MODE: import.meta.env.MODE,
    VITE_MUI_PRO_LICENCE_KEY: import.meta.env.VITE_MUI_PRO_LICENCE_KEY,
    VITE_ONEDRIVE_URL: import.meta.env.VITE_ONEDRIVE_URL,
    VITE_FLAGSMITH_ENV_ID: import.meta.env.VITE_FLAGSMITH_ENV_ID,
    VITE_FLAGSMITH_LISTENING_INTERVAL: import.meta.env.VITE_FLAGSMITH_LISTENING_INTERVAL,
    VITE_BASE_PATH: import.meta.env.VITE_BASE_PATH,
  };
};

const getRuntimeEnvironment = () => {
  const env = window.__env__ || {};

  const envVars = {
    VITE_AUTH0_DOMAIN: env.VITE_AUTH0_DOMAIN,
    VITE_AUTH0_CLIENT_ID: env.VITE_AUTH0_CLIENT_ID,
    VITE_AUTH0_AUDIENCE: env.VITE_AUTH0_AUDIENCE,
    VITE_AUTH0_SCOPE: env.VITE_AUTH0_SCOPE,
    VITE_AUTH_DEV: parseEnvBoolean(env.VITE_AUTH_DEV),
    VITE_MAPBOX_ACCESS_TOKEN: env.VITE_MAPBOX_ACCESS_TOKEN,
    VITE_API_URL: env.VITE_API_URL,
    VITE_GRAFANA_SCAN_DASHBOARD_URL: env.VITE_GRAFANA_SCAN_DASHBOARD_URL,
    VITE_DATACY: env.VITE_DATACY,
    VITE_ENABLE_MOCK_API: env.VITE_ENABLE_MOCK_API
      ? env.VITE_ENABLE_MOCK_API === 'true'
      : undefined,
    VITE_TRENCHVIEWER_FRONTEND_URL: env.VITE_TRENCHVIEWER_FRONTEND_URL,
    VITE_API_BACKOFFICE: env.VITE_API_BACKOFFICE,
    MODE: env.MODE,
    VITE_MUI_PRO_LICENCE_KEY: env.VITE_MUI_PRO_LICENCE_KEY,
    VITE_ONEDRIVE_URL: env.VITE_ONEDRIVE_URL,
    VITE_FLAGSMITH_ENV_ID: env.VITE_FLAGSMITH_ENV_ID,
    VITE_FLAGSMITH_LISTENING_INTERVAL: env.VITE_FLAGSMITH_LISTENING_INTERVAL,
    VITE_BASE_PATH: env.VITE_BASE_PATH,
  };

  // Filter out entries with undefined values
  const definedVars = Object.fromEntries(
    Object.entries(envVars).filter(([, value]) => value !== undefined),
  );

  return definedVars;
};

const dotEnv = {
  ...getBuildEnvironment(),
  ...getRuntimeEnvironment(),
};

export const environment: Environment = {
  auth0: {
    domain: dotEnv.VITE_AUTH0_DOMAIN as string,
    clientId: dotEnv.VITE_AUTH0_CLIENT_ID as string,
    authorizationParams: {
      audience: dotEnv.VITE_AUTH0_AUDIENCE as string,
      scope: dotEnv.VITE_AUTH0_SCOPE as string,
      redirect_uri: window.location.origin,
    },
  },
  authDev: parseEnvBoolean(dotEnv.VITE_AUTH_DEV),
  mapBox: {
    accessToken: dotEnv.VITE_MAPBOX_ACCESS_TOKEN,
  },
  isDev: dotEnv.MODE === 'development',
  apiUrl: dotEnv.VITE_API_BACKOFFICE,
  scanDashboardUrl: dotEnv.VITE_GRAFANA_SCAN_DASHBOARD_URL,
  dataCy: !!dotEnv.VITE_DATACY,
  useMockApi: dotEnv.VITE_ENABLE_MOCK_API === 'true',
  trenchviewerFrontendUrl: dotEnv.VITE_TRENCHVIEWER_FRONTEND_URL,
  unifiedApiUrl: dotEnv.VITE_API_URL,
  muiProLicenceKey: dotEnv.VITE_MUI_PRO_LICENCE_KEY,
  oneDriveUrl: dotEnv.VITE_ONEDRIVE_URL,
  flagsmithEnvId: dotEnv.VITE_FLAGSMITH_ENV_ID,
  flagsmithListeningInterval: dotEnv.VITE_FLAGSMITH_LISTENING_INTERVAL,
  basePath: dotEnv.VITE_BASE_PATH,
};
